import React, { useEffect, useState, useRef } from 'react';
import { useMessages, useSendMessage, useEditMessage, useDeleteMessage, useReportMessage, queryKeys } from '@/hooks/api';
import { useAppStore, useUI } from '@/store';
import { Button, Avatar, LoadingScreen } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { formatMessageTime } from '@/utils';
import { getOtherUser } from '@/utils/mappers';
import { cn } from '@/utils';
import type { Message, Match, MessageCreate } from '@/types';
import { api } from '@/api';
import { useQueryClient } from '@tanstack/react-query';
import ProfileDetailScreen from './ProfileDetailScreen';

const ChatScreen: React.FC = () => {
  const { user } = useAppStore();
  const { modalData, closeModal, setActiveScreen } = useUI();
  const [messageText, setMessageText] = useState('');
  const [matchData, setMatchData] = useState<Match | null>(null);
  const [viewingProfile, setViewingProfile] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  
  // Новые states для Messaging 2.0
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [contextMenu, setContextMenu] = useState<{
    message: Message;
    position: { x: number; y: number };
  } | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [fullscreenPhoto, setFullscreenPhoto] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();
  const previousScrollHeightRef = useRef<number>(0);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  
  const matchId = modalData?.matchId;
  const messagesQuery = useMessages(matchId, { enabled: !!matchId });
  const sendMessage = useSendMessage();
  const editMessage = useEditMessage();
  const deleteMessage = useDeleteMessage();
  const reportMessage = useReportMessage();
  
  // Extract data from infinite query result
  const isLoading = messagesQuery.isLoading;
  const fetchNextPage = messagesQuery.fetchNextPage;
  const hasNextPage = messagesQuery.hasNextPage;
  const isFetchingNextPage = messagesQuery.isFetchingNextPage;
  
  // Flatten pages into messages array
  const messagesList: Message[] = (messagesQuery.data?.pages?.flat() || []) as Message[];

  // Load match data to get user info
  useEffect(() => {
    if (matchId && modalData?.match) {
      setMatchData(modalData.match);
    }
  }, [matchId, modalData]);

  // Функция для прокрутки в самый низ
  const scrollToBottom = (behavior: 'auto' | 'smooth' = 'smooth') => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesList.length > 0) {
      // Небольшая задержка для рендера фото и reply элементов
      const scrollTimer = setTimeout(() => {
        scrollToBottom('smooth');
      }, 150);
      
      return () => clearTimeout(scrollTimer);
    }
  }, [messagesList.length]); // Только при изменении количества

  // Initial scroll to bottom when chat opens
  useEffect(() => {
    if (messagesList.length > 0) {
      // Мгновенный скролл при открытии
      scrollToBottom('auto');
      
      // Повторный через 500ms чтобы точно учесть все загруженные элементы
      const timer = setTimeout(() => {
        scrollToBottom('auto');
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [matchId]); // Только при смене чата

  // Mark messages as read when chat opens
  useEffect(() => {
    if (messagesList && messagesList.length > 0 && user) {
      const unreadMessages = messagesList.filter(
        (msg: Message) => !msg.read && msg.senderId !== user.id
      );
      
      if (unreadMessages.length > 0) {
        // Mark all unread messages as read
        let markedCount = 0;
        unreadMessages.forEach(async (msg: Message) => {
          try {
            await api.markMessageAsRead(Number(msg.id));
            markedCount++;
            
            // After marking all messages, invalidate counters once
            if (markedCount === unreadMessages.length) {
              queryClient.invalidateQueries(queryKeys.notificationCounters);
              queryClient.invalidateQueries(queryKeys.matches);
            }
          } catch (error) {
            console.error('Failed to mark message as read:', error);
          }
        });
      }
    }
  }, [messagesList, user, queryClient]);

  // Handle viewport changes for keyboard visibility
  useEffect(() => {
    const handleViewportChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.height) {
        setViewportHeight(customEvent.detail.height);
      }
    };

    const handleResize = () => {
      // Fallback for browsers that don't fire telegram-viewport-changed
      if (window.visualViewport) {
        setViewportHeight(window.visualViewport.height);
      } else {
        setViewportHeight(window.innerHeight);
      }
    };

    window.addEventListener('telegram-viewport-changed', handleViewportChange);
    window.addEventListener('resize', handleResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('telegram-viewport-changed', handleViewportChange);
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  // Handle scroll for pagination (load older messages)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Check if user scrolled near the top
      const scrollTop = container.scrollTop;
      const threshold = 100; // px from top

      if (scrollTop < threshold && hasNextPage && !isFetchingNextPage) {
        // Save current scroll height to restore position after prepending
        previousScrollHeightRef.current = container.scrollHeight;
        fetchNextPage();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Maintain scroll position when loading older messages
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !isFetchingNextPage) return;

    // After new messages are loaded, adjust scroll to maintain position
    const observer = new MutationObserver(() => {
      if (previousScrollHeightRef.current > 0) {
        const newScrollHeight = container.scrollHeight;
        const scrollDiff = newScrollHeight - previousScrollHeightRef.current;
        container.scrollTop = container.scrollTop + scrollDiff;
        previousScrollHeightRef.current = 0;
      }
    });

    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isFetchingNextPage]);

  // Configure Telegram buttons
  useEffect(() => {
    TelegramUtils.configureBackButton(true, () => {
      closeModal();
      setActiveScreen('matches');
    });
    TelegramUtils.configureMainButton({ visible: false });

    return () => {
      TelegramUtils.configureBackButton(false);
    };
  }, [closeModal, setActiveScreen]);

  const handleSendMessage = async () => {
    if ((!messageText.trim() && !photoPreviewUrl) || !matchId) return;
    
    const text = messageText.trim();
    const messageData: MessageCreate = {
      content: text || undefined,
      photo_url: photoPreviewUrl || undefined,
      reply_to_message_id: replyTo ? Number(replyTo.id) : undefined,
    };
    
    setMessageText('');
    setPhotoPreviewUrl(null);
    setSelectedPhoto(null);
    setReplyTo(null);
    
    // Сбросить высоту textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    sendMessage.mutate({ 
      matchId, 
      data: messageData,
      replyTo 
    });
    
    // Возвращаем фокус на textarea для быстрой отправки следующего сообщения
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  // Auto-resize textarea
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageText(e.target.value);
    
    // Auto-resize
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Enter теперь делает перенос строки, отправка только по кнопке
    // Для отправки используем Ctrl+Enter или Cmd+Enter
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTextareaFocus = () => {
    // Scroll textarea into view when keyboard appears
    setTimeout(() => {
      textareaRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'nearest' 
      });
    }, 300); // Delay to allow keyboard to appear
  };

  // Обработчики для новых функций
  const handleMessageAction = (message: Message, action: string) => {
    switch (action) {
      case 'reply':
        setReplyTo(message);
        textareaRef.current?.focus();
        TelegramUtils.haptic.selection();
        break;
      case 'edit':
        setEditingMessageId(message.id);
        setEditingText(message.text || '');
        TelegramUtils.haptic.selection();
        break;
      case 'delete':
        handleDeleteMessage(message.id);
        break;
      case 'report':
        handleReportMessage(message.id);
        break;
    }
    setContextMenu(null);
  };

  const handleDeleteMessage = async (messageId: string) => {
    const confirmed = await TelegramUtils.showConfirm(
      'Удалить сообщение? Оно будет заменено на "Сообщение удалено"'
    );
    
    if (confirmed && matchId) {
      deleteMessage.mutate({ matchId, messageId: Number(messageId) });
    }
  };

  const handleReportMessage = async (messageId: string) => {
    const buttonId = await TelegramUtils.showPopup(
      'Почему вы хотите пожаловаться на это сообщение?',
      'Жалоба на сообщение',
      [
        { id: 'spam', text: 'Спам' },
        { id: 'inappropriate', text: 'Неприемлемый контент' },
        { id: 'harassment', text: 'Домогательства' },
        { id: 'cancel', text: 'Отмена', type: 'cancel' },
      ]
    );
    
    if (buttonId && buttonId !== 'cancel') {
      const reasonMap: Record<string, string> = {
        'spam': 'Спам',
        'inappropriate': 'Неприемлемый контент',
        'harassment': 'Домогательства'
      };
      
      const reasonText = reasonMap[buttonId] || 'Неприемлемый контент';
      reportMessage.mutate({ messageId: Number(messageId), reason: reasonText });
    }
  };

  const handleSaveEdit = (messageId: string) => {
    if (!editingText.trim() || !matchId) return;
    
    editMessage.mutate({
      matchId,
      messageId: Number(messageId),
      content: editingText.trim()
    });
    
    setEditingMessageId(null);
    setEditingText('');
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingText('');
  };

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Проверка размера и типа
    if (file.size > 10 * 1024 * 1024) { // 10MB
      await TelegramUtils.showAlert('Файл слишком большой. Максимум 10 МБ');
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      await TelegramUtils.showAlert('Можно отправлять только изображения');
      return;
    }
    
    setSelectedPhoto(file);
    
    // Создаем preview
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target?.result as string;
      
      // Upload to server using upload-only endpoint (doesn't add to profile)
      try {
        const { url } = await api.uploadPhotoOnly(file);
        setPhotoPreviewUrl(url);
        TelegramUtils.haptic.success();
      } catch (error) {
        console.error('Failed to upload photo:', error);
        await TelegramUtils.showAlert('Не удалось загрузить фото');
        setSelectedPhoto(null);
        TelegramUtils.haptic.error();
      }
    };
    reader.readAsDataURL(file);
  };

  const handleLongPress = (message: Message, event: React.TouchEvent | React.MouseEvent) => {
    if (message.deletedAt) return; // Нельзя взаимодействовать с удаленными
    
    const touch = 'touches' in event ? event.touches[0] : event as React.MouseEvent;
    
    longPressTimer.current = setTimeout(() => {
      setContextMenu({
        message,
        position: {
          x: touch.clientX,
          y: touch.clientY,
        }
      });
      TelegramUtils.haptic.medium();
    }, 300);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  if (isLoading) {
    return <LoadingScreen message="Загружаем чат..." />;
  }

  if (!matchData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-tg-bg p-6">
        <div className="text-center">
          <p className="text-white mb-4">Чат не найден</p>
          <Button onClick={() => setActiveScreen('matches')}>
            Вернуться к совпадениям
          </Button>
        </div>
      </div>
    );
  }

  const currentUserId = user?.id || '0';
  const otherUser = getOtherUser(matchData, currentUserId);

  // Show ProfileDetailScreen if viewing profile
  if (viewingProfile && otherUser) {
    return (
      <ProfileDetailScreen
        userId={otherUser.id}
        matchId={matchId}
        onBack={() => {
          // Just return to chat, don't close it
          setViewingProfile(false);
        }}
        onUnmatch={() => {
          // After successful unmatch, close chat and go to matches list
          setViewingProfile(false);
          closeModal();
          setActiveScreen('matches');
        }}
        onSwipe={(direction) => {
          // Just close profile view, match already exists
          setViewingProfile(false);
        }}
      />
    );
  }

  return (
    <div 
      className="flex flex-col bg-tg-bg safe-area-y"
      style={{ height: `${viewportHeight}px` }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-tg-bg/95 backdrop-blur-xl">
        <button
          onClick={() => {
            closeModal();
            setActiveScreen('matches');
          }}
          className="p-2 -ml-2 hover:bg-white/5 rounded-xl transition-colors"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <button
          onClick={() => {
            setViewingProfile(true);
            TelegramUtils.haptic.selection();
          }}
          className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity"
        >
          <Avatar src={otherUser.photos[0]} name={otherUser.name} size="md" />
          
          <div className="flex-1 text-left">
            <h2 className="font-semibold text-white">{otherUser.name}, {otherUser.age}</h2>
            <p className="text-xs text-neutral-500">Онлайн недавно</p>
          </div>
        </button>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Loading indicator at top when fetching older messages */}
        {isFetchingNextPage && (
          <div className="flex justify-center py-2">
            <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
          </div>
        )}
        
        {messagesList.length === 0 && !isFetchingNextPage && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">💕</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Совпадение!</h3>
            <p className="text-neutral-400 text-sm">
              Скажите привет и начните знакомство
            </p>
          </div>
        )}
        
        {messagesList.map((message: Message) => {
          const isEditing = editingMessageId === message.id;
          
          return isEditing ? (
            <EditableMessageBubble
              key={message.id}
              message={message}
              isOwn={message.senderId === currentUserId}
              editingText={editingText}
              onTextChange={setEditingText}
              onSave={() => handleSaveEdit(message.id)}
              onCancel={handleCancelEdit}
            />
          ) : (
            <MessageBubble 
              key={message.id} 
              message={message} 
              isOwn={message.senderId === currentUserId}
              onLongPress={(e) => handleLongPress(message, e)}
              onLongPressEnd={handleLongPressEnd}
              onPhotoClick={(photoUrl) => setFullscreenPhoto(photoUrl)}
            />
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-white/10 bg-tg-bg/95 backdrop-blur-xl safe-area-bottom">
        {/* Reply Preview */}
        {replyTo && (
          <div className="mb-3 bg-white/10 border-l-2 border-white/30 rounded-lg p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white/70 mb-1 font-medium">Ответ на:</div>
                <div className="text-sm text-white font-medium truncate">
                  {replyTo.text || '📷 Фото'}
                </div>
              </div>
              <button 
                onClick={() => setReplyTo(null)}
                className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Photo Preview */}
        {photoPreviewUrl && (
          <div className="mb-3 relative">
            <img 
              src={photoPreviewUrl} 
              alt="Preview" 
              className="max-h-32 rounded-lg"
            />
            <button
              onClick={() => {
                setPhotoPreviewUrl(null);
                setSelectedPhoto(null);
              }}
              className="absolute top-1 right-1 w-6 h-6 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70"
            >
              ✕
            </button>
          </div>
        )}

        <div className="flex items-center gap-2">
          {/* Photo button */}
          <label className="flex-shrink-0 h-[44px] w-[44px] bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center hover:bg-white/10 transition-all active:scale-95 cursor-pointer">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <input 
              type="file" 
              accept="image/*"
              onChange={handlePhotoSelect}
              className="hidden"
            />
          </label>

          <textarea
            ref={textareaRef}
            value={messageText}
            onChange={handleTextChange}
            onKeyPress={handleKeyPress}
            onFocus={handleTextareaFocus}
            placeholder="Сообщение..."
            maxLength={5000}
            className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-white/20 resize-none overflow-hidden"
            rows={1}
            style={{
              minHeight: '44px',
              maxHeight: '120px',
            }}
          />
          <button
            onClick={handleSendMessage}
            disabled={!messageText.trim() && !photoPreviewUrl}
            className="flex-shrink-0 h-[44px] w-[44px] bg-white text-black rounded-2xl font-semibold hover:bg-neutral-100 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-lg"
          >
            →
          </button>
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <MessageContextMenu
          message={contextMenu.message}
          isOwn={contextMenu.message.senderId === currentUserId}
          position={contextMenu.position}
          onAction={handleMessageAction}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Photo Viewer */}
      {fullscreenPhoto && (
        <PhotoViewer
          photoUrl={fullscreenPhoto}
          onClose={() => setFullscreenPhoto(null)}
        />
      )}
    </div>
  );
};

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
  onLongPress: (e: React.TouchEvent | React.MouseEvent) => void;
  onLongPressEnd: () => void;
  onPhotoClick?: (photoUrl: string) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = React.memo(({ message, isOwn, onLongPress, onLongPressEnd, onPhotoClick }) => {
  const isDeleted = message.deletedAt !== null;
  const hasReply = message.repliedMessage !== null;
  const hasPhoto = message.photoUrl && !isDeleted;
  const hasTextOnly = message.text && !hasPhoto;

  return (
    <div className={cn('flex', isOwn ? 'justify-end' : 'justify-start')}>
      <div className="max-w-[75%]">
        {/* Reply indicator */}
        {hasReply && !isDeleted && (
          <div className="mb-1 ml-3 mr-3 p-2 rounded-lg bg-white/10 border-l-2 border-white/30">
            <div className="text-xs text-white font-medium truncate">
              {message.repliedMessage?.text || '📷 Фото'}
            </div>
          </div>
        )}

        {/* Photo message - отдельный layout */}
        {hasPhoto ? (
          <div
            className="select-none"
            onTouchStart={onLongPress}
            onTouchEnd={onLongPressEnd}
            onMouseDown={onLongPress}
            onMouseUp={onLongPressEnd}
            onMouseLeave={onLongPressEnd}
          >
            <img 
              src={message.photoUrl!}
              alt="Message photo"
              className="rounded-2xl max-w-full cursor-pointer active:opacity-90 transition-opacity"
              loading="lazy"
              onClick={(e) => {
                e.stopPropagation();
                onPhotoClick?.(message.photoUrl!);
              }}
            />
            
            {/* Text caption if exists */}
            {message.text && (
              <div
                className={cn(
                  'mt-2 px-4 py-2.5 rounded-2xl',
                  isOwn
                    ? 'bg-white text-black rounded-br-md'
                    : 'bg-white/10 text-white rounded-bl-md border border-white/10'
                )}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                  {message.text}
                </p>
              </div>
            )}
            
            {/* Metadata under photo */}
            <div className={cn(
              'flex items-center gap-1.5 mt-1 px-1',
              isOwn ? 'justify-end' : 'justify-start'
            )}>
              <p className="text-xs text-white/60">
                {formatMessageTime(message.timestamp)}
              </p>
              {message.isEdited && (
                <span className="text-xs text-white/50">изм.</span>
              )}
              {isOwn && (
                <span 
                  className={cn(
                    'text-xs inline-block',
                    message.read ? 'text-primary-400' : 'text-white/40'
                  )}
                  style={{ letterSpacing: '-0.5em' }}
                >
                  {message.read ? '✓✓' : '✓'}
                </span>
              )}
            </div>
          </div>
        ) : (
          /* Text-only message */
          <div
            className={cn(
              'px-4 py-2.5 rounded-2xl select-none',
              isOwn
                ? 'bg-white text-black rounded-br-md'
                : 'bg-white/10 text-white rounded-bl-md border border-white/10',
              isDeleted && 'opacity-70'
            )}
            onTouchStart={onLongPress}
            onTouchEnd={onLongPressEnd}
            onMouseDown={onLongPress}
            onMouseUp={onLongPressEnd}
            onMouseLeave={onLongPressEnd}
          >
            {/* Text content */}
            {isDeleted ? (
              <p className="text-sm leading-relaxed italic text-neutral-500">
                Сообщение удалено
              </p>
            ) : (
              <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                {message.text}
              </p>
            )}

            {/* Metadata */}
            <div className={cn(
              'flex items-center gap-1.5 mt-1',
              isOwn ? 'justify-end' : 'justify-start'
            )}>
              <p className={cn(
                'text-xs',
                isOwn ? 'text-black/60' : 'text-neutral-500'
              )}>
                {formatMessageTime(message.timestamp)}
              </p>
              {message.isEdited && !isDeleted && (
                <span className={cn(
                  'text-xs',
                  isOwn ? 'text-black/50' : 'text-neutral-600'
                )}>
                  изм.
                </span>
              )}
              {isOwn && (
                <span className={cn(
                  'text-xs inline-block',
                  message.read ? 'text-primary-600' : 'text-black/40'
                )}
                style={{ letterSpacing: '-0.5em' }}
                >
                  {message.read ? '✓✓' : '✓'}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}, (prev, next) => {
  return prev.message.id === next.message.id &&
         prev.message.text === next.message.text &&
         prev.message.isEdited === next.message.isEdited &&
         prev.message.read === next.message.read &&
         prev.message.deletedAt === next.message.deletedAt &&
         prev.message.photoUrl === next.message.photoUrl;
});

// Editable Message Bubble Component
interface EditableMessageBubbleProps {
  message: Message;
  isOwn: boolean;
  editingText: string;
  onTextChange: (text: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

const EditableMessageBubble: React.FC<EditableMessageBubbleProps> = ({ 
  message, 
  isOwn, 
  editingText, 
  onTextChange, 
  onSave, 
  onCancel 
}) => {
  return (
    <div className={cn('flex', isOwn ? 'justify-end' : 'justify-start')}>
      <div className="max-w-[75%] w-full">
        <div
          className={cn(
            'px-4 py-2.5 rounded-2xl',
            isOwn
              ? 'bg-white text-black rounded-br-md'
              : 'bg-white/10 text-white rounded-bl-md border border-white/10'
          )}
        >
          <textarea
            value={editingText}
            onChange={(e) => onTextChange(e.target.value)}
            className={cn(
              'w-full bg-transparent border-none outline-none resize-none text-sm leading-relaxed',
              isOwn ? 'text-black' : 'text-white'
            )}
            rows={3}
            maxLength={5000}
            autoFocus
          />
          
          <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t border-current/10">
            <button
              onClick={onCancel}
              className={cn(
                'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                isOwn 
                  ? 'bg-black/10 text-black hover:bg-black/20'
                  : 'bg-white/10 text-white hover:bg-white/20'
              )}
            >
              Отмена
            </button>
            <button
              onClick={onSave}
              className={cn(
                'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                isOwn 
                  ? 'bg-black text-white hover:bg-black/90'
                  : 'bg-white text-black hover:bg-white/90'
              )}
            >
              ✓ Сохранить
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Context Menu Component
interface MessageContextMenuProps {
  message: Message;
  isOwn: boolean;
  position: { x: number; y: number };
  onAction: (message: Message, action: string) => void;
  onClose: () => void;
}

const MessageContextMenu: React.FC<MessageContextMenuProps> = ({ 
  message, 
  isOwn, 
  position, 
  onAction, 
  onClose 
}) => {
  const actions = isOwn 
    ? [
        { icon: '↩️', label: 'Ответить', action: 'reply' },
        { icon: '✏️', label: 'Изменить', action: 'edit' },
        { icon: '🗑', label: 'Удалить', action: 'delete', danger: true }
      ]
    : [
        { icon: '↩️', label: 'Ответить', action: 'reply' },
        { icon: '⚠️', label: 'Пожаловаться', action: 'report', danger: true }
      ];

  return (
    <div 
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="absolute bg-tg-bg/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/10 overflow-hidden animate-scaleIn"
        style={{ 
          top: Math.min(position.y, window.innerHeight - 200),
          left: Math.min(position.x, window.innerWidth - 200),
          transformOrigin: 'top left'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-2 space-y-1 min-w-[180px]">
          {actions.map((action) => (
            <button
              key={action.action}
              onClick={() => onAction(message, action.action)}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors',
                action.danger
                  ? 'text-danger-500 hover:bg-danger-500/10'
                  : 'text-white hover:bg-white/10'
              )}
            >
              <span className="text-lg">{action.icon}</span>
              <span className="text-sm font-medium">{action.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Photo Viewer Component with Zoom
interface PhotoViewerProps {
  photoUrl: string;
  onClose: () => void;
}

const PhotoViewer: React.FC<PhotoViewerProps> = ({ photoUrl, onClose }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const lastTapRef = useRef(0);

  // Reset on close
  useEffect(() => {
    return () => {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    };
  }, []);

  // Double tap to zoom
  const handleDoubleTap = () => {
    const now = Date.now();
    const timeSinceLastTap = now - lastTapRef.current;
    
    if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
      // Double tap detected
      if (scale === 1) {
        setScale(2);
        TelegramUtils.haptic.light();
      } else {
        setScale(1);
        setPosition({ x: 0, y: 0 });
        TelegramUtils.haptic.light();
      }
    }
    
    lastTapRef.current = now;
  };

  // Pinch to zoom
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging && e.touches.length === 1) {
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y,
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Mouse events for desktop
  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Wheel zoom for desktop
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY * -0.01;
    const newScale = Math.min(Math.max(1, scale + delta), 4);
    setScale(newScale);
    
    if (newScale === 1) {
      setPosition({ x: 0, y: 0 });
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black z-[100] flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget && scale === 1) {
          onClose();
        }
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
      >
        ✕
      </button>

      {/* Photo container */}
      <div
        ref={containerRef}
        className="w-full h-full flex items-center justify-center overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <img
          ref={imageRef}
          src={photoUrl}
          alt="Full screen"
          className={cn(
            "max-w-full max-h-full object-contain transition-transform",
            isDragging ? 'cursor-grabbing' : scale > 1 ? 'cursor-grab' : 'cursor-pointer'
          )}
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            transitionDuration: isDragging ? '0ms' : '200ms',
          }}
          onClick={handleDoubleTap}
          draggable={false}
        />
      </div>

      {/* Zoom indicator */}
      {scale > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-sm">
          {Math.round(scale * 100)}%
        </div>
      )}
    </div>
  );
};

export default ChatScreen;
